import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Layer, Document, Folder, Trash, Edit } from 'iconsax-reactjs';
import type { Category } from '../../../services/webServices/category/types';

interface CategoryTreeProps {
  categories: Category[];
  onEdit: (category: Category) => void;
  onDelete: (id: number) => void;
};

type ParentItemProps = {
  parent: Category;
  isOpen: boolean;
  onToggle: () => void;
  onEdit: (category: Category) => void;
  onDelete: (id: number) => void;
};

type ChildItemProps = {
  child: Category;
  onEdit: (category: Category) => void;
  onDelete: (id: number) => void;
};

type GrandChildItemProps ={
  grand: Category;
  onEdit: (category: Category) => void;
  onDelete: (id: number) => void;
};

const isNonEmpty = (arr?: Category[]) => Array.isArray(arr) && arr.length > 0;

function ParentItem({ parent, isOpen, onToggle, onEdit, onDelete }: ParentItemProps) {

  const navigate = useNavigate();

  const handleEdit = (id: number) => {
    navigate(`/add-category/${id}`);
  };

  return (
    <div role="treeitem" aria-expanded={isOpen} className="px-2 py-2 rounded-md hover:bg-gray-50">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onToggle}
            className="p-1 rounded hover:bg-gray-100"
            aria-label={isOpen ? 'بستن' : 'باز کردن'}
          >
            <span className="inline-flex items-center justify-center w-5 h-5 text-gray-700 select-none">
              {isOpen ? '-' : '+'}
            </span>
          </button>
          <Layer variant={isOpen ? 'Bold' : 'Linear'} size="18" className="text-gray-800" />
          <span className="font-semibold cursor-default text-sm text-right text-gray-800">
            {parent.title}
          </span>
        </div>
        <div className="flex">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(parent.id);
            }}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="ویرایش"
          >
            <Edit size={18} className="text-blue-500" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(parent.id);
            }}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="حذف"
          >
            <Trash size={18} className="text-red-500" />
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="mt-1">
          <div className="flex">
            <div className="w-6 mr-2 border-r border-gray-300" />
            <div className="flex-1 pr-2">
              {parent.children && isNonEmpty(parent.children) ? (
                parent.children.map((child: Category) => (
                  <ChildItem
                    key={child.id}
                    child={child}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))
              ) : (
                <div className="py-0.5 px-1.5 rounded-md">
                  <span className="text-xs text-gray-500">بدون دسته بندی</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function ChildItem({ child, onEdit, onDelete }: ChildItemProps) {

  const navigate = useNavigate();

  const handleEdit = (id: number) => {
    navigate(`/add-category/${id}`);
  };

  const [open, setOpen] = React.useState(false);
  return (
    <div role="treeitem" aria-expanded={open} className="px-2 py-2 rounded-md hover:bg-gray-50">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="p-1 rounded hover:bg-gray-100"
            aria-label={open ? 'بستن' : 'باز کردن'}
          >
            <span className="inline-flex items-center justify-center w-5 h-5 text-gray-700 select-none">
              {open ? '-' : '+'}
            </span>
          </button>
          <Document variant={open ? 'Bold' : 'Linear'} size="18" className="text-gray-700" />
          <span className="font-normal cursor-default text-sm text-right text-gray-700">
            {child.title}
          </span>
        </div>
        <div className="flex">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(child.id);
            }}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="ویرایش"
          >
            <Edit size={18} className="text-blue-500" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(child.id);
            }}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="حذف"
          >
            <Trash size={18} className="text-red-500" />
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-1">
          <div className="flex">
            <div className="w-10 mr-2 border-r border-gray-300" />
            <div className="flex-1 pr-2">
              {child.children && isNonEmpty(child.children) ? (
                child.children.map((grand: Category) => (
                  <GrandChildItem
                    key={grand.id}
                    grand={grand}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))
              ) : (
                <div className="py-0.5 px-1.5 rounded-md ">
                  <span className="text-xs text-gray-500">بدون زیرمجموعه</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function GrandChildItem({ grand, onEdit, onDelete }: GrandChildItemProps) {

  const navigate = useNavigate();

  const handleEdit = (id: number) => {
    navigate(`/add-category/${id}`);
  };

  return (
    <div className="px-2 py-2 rounded-md cursor-pointer" role="treeitem">
      <div className="flex items-center justify-between w-full cursor-default">
        <div className="flex items-center gap-2">
          <Folder size={18} className="text-gray-500 " />
          <span className="text-sm text-gray-500  ">{grand.title}</span>
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(grand.id);
            }}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="ویرایش"
          >
            <Edit size={18} className="text-blue-500" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(grand.id);
            }}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="حذف"
          >
            <Trash size={18} className="text-red-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function CategoryTree({ categories, onEdit, onDelete }: CategoryTreeProps) {

  const [openParents, setOpenParents] = React.useState<Set<number>>(new Set());

  if (!categories?.length) return null;

  const toggleParent = (id: number) => {
    setOpenParents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="bg-white rounded-sm p-1 px-3" role="tree" aria-orientation="vertical">
      {categories.map((parent) => (
        <ParentItem
          key={parent.id}
          parent={parent}
          isOpen={openParents.has(parent.id)}
          onToggle={() => toggleParent(parent.id)}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};